import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArticleBrands, useCreateBrand, useUpdateBrand, useDeleteBrand } from '@/hooks/use-articles';
import { ImageUpload } from '@/components/ui/image-upload';
import {
  Button, Card, CardContent,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  EmptyState, Input, Label, PageHeader,
  Skeleton,
} from '@erp/ui';
import { Plus, Trash2, Pencil, Star } from '@/components/icons';
import { ArticleAssignDialog } from './ArticleAssignDialog';
import type { ArticleBrand } from '@erp/data-access/interfaces';

export function BrandsPage() {
  const { t } = useTranslation();
  const { data: brands, isLoading } = useArticleBrands();
  const createMutation = useCreateBrand();
  const updateMutation = useUpdateBrand();
  const deleteMutation = useDeleteBrand();

  const [formOpen, setFormOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<ArticleBrand | null>(null);
  const [editing, setEditing] = useState<ArticleBrand | null>(null);
  const [name, setName] = useState('');
  const [image, setImage] = useState<string | null>(null);

  const openCreate = () => { setEditing(null); setName(''); setImage(null); setFormOpen(true); };
  const openEdit = (b: ArticleBrand) => { setEditing(b); setName(b.name); setImage(b.imageUrl); setFormOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, name: name.trim(), imageUrl: image ?? undefined });
    } else {
      await createMutation.mutateAsync({ name: name.trim(), imageUrl: image ?? undefined });
    }
    setFormOpen(false); setEditing(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('articles.brands')}
        actions={
          <Button onClick={openCreate}>
            <Plus data-icon="inline-start" size={16} />
            {t('articles.newBrand')}
          </Button>
        }
      />

      <Card className="transition-all duration-200">
        <CardContent className="py-4">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="rounded-lg border bg-card p-3 space-y-2">
                  <Skeleton className="w-full h-20 rounded" />
                  <Skeleton className="h-4 w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          ) : !brands?.length ? (
            <EmptyState
              icon={<Star size={18} />}
              title={t('common.noResults')}
              action={<Button variant="outline" size="sm" onClick={openCreate}>
                  <Plus data-icon="inline-start" size={14} />
                  {t('articles.newBrand')}
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {brands.map((b) => (
                <div
                  key={b.id}
                  className="rounded-lg border bg-card p-3 space-y-2 group relative transition-all duration-200 hover:border-foreground/20 hover:shadow-sm animate-in fade-in slide-in-from-bottom-1 fill-mode-both"
                >
                  {b.imageUrl ? (
                    <img src={b.imageUrl} alt={b.name} className="w-full h-20 object-contain rounded" />
                  ) : (
                    <div className="w-full h-20 rounded bg-muted flex items-center justify-center text-2xl font-bold text-muted-foreground/40">
                      {b.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <p className="text-sm font-medium text-center truncate">{b.name}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs h-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => { setAssignTarget(b); setAssignOpen(true); }}
                  >
                    <Plus size={12} className="me-1" />{t('articles.assignArticle')}
                  </Button>
                  <div className="absolute top-1.5 end-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon-xs" className="bg-background/80" onClick={() => openEdit(b)}>
                      <Pencil size={12} />
                    </Button>
                    <Button variant="ghost" size="icon-xs" className="bg-background/80" onClick={() => deleteMutation.mutate(b.id)}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? t('common.edit') : t('articles.newBrand')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t('common.name')} *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('articles.image')}</Label>
              <ImageUpload value={image} onChange={setImage} bucket="erp-images" pathPrefix="brands/" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={!name.trim() || createMutation.isPending || updateMutation.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {assignTarget && (
        <ArticleAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          field="brand"
          targetId={assignTarget.id}
          title={`${t('articles.assignArticle')} — ${assignTarget.name}`}
        />
      )}
    </div>
  );
}
